'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children"];
import * as React from 'react';
import Markdown, { RuleType } from 'markdown-to-jsx';
import { useMessageContentTabIndex } from '@mui/x-chat-headless';
import { normalizeMarkdownForRender } from '@mui/x-chat-headless/internals';
import { ChatCodeBlock } from "../ChatCodeBlock/index.mjs";
import { useStreamingMarkdownRepair } from "../internals/streamingMarkdownRepair.mjs";

// Kept in sync with the headless `safeUri` allow-list so links behave the same
// across markdown and source/file parts.
import { jsx as _jsx } from "react/jsx-runtime";
const SAFE_URL_PROTOCOLS = ['http:', 'https:', 'mailto:', 'tel:'];

// Applied by markdown-to-jsx to every link `href` / image `src`. Returns the value
// when safe, or `null` to drop the attribute — so a `javascript:`/`data:`/
// protocol-relative URL (or remend's `streamdown:incomplete-link` placeholder for a
// half-streamed link) renders as inert text rather than a navigable target.
const sanitizer = value => {
  try {
    const parsed = new URL(value);
    return SAFE_URL_PROTOCOLS.includes(parsed.protocol) ? value : null;
  } catch {
    // Relative URLs (no scheme) are allowed; reject protocol-relative `//host`,
    // which resolves to an external origin despite carrying no explicit scheme.
    if (!value.includes(':') && !/^[/\\]{2}/.test(value)) {
      return value;
    }
    return null;
  }
};

// Markdown links open in a new tab (the sanitizer above already neutralised the
// href) and participate in the message list's drill-in model: inside a roving
// list they stay out of the tab order until the user drills into the message
// with Enter, keeping the whole list a single Tab stop. Mouse clicks work
// throughout, and outside a roving list the tab order is untouched.
function MarkdownLink(_ref) {
  let {
      children
    } = _ref,
    other = _objectWithoutPropertiesLoose(_ref, _excluded);
  const contentTabIndex = useMessageContentTabIndex();
  return /*#__PURE__*/_jsx("a", _extends({
    target: "_blank",
    rel: "noopener noreferrer",
    tabIndex: contentTabIndex
  }, other, {
    children: children
  }));
}
const markdownOptions = {
  // Always emit block-level wrappers (so a lone line becomes a `<p>`, matching the
  // chat bubble's `& p`/`& pre` styling), but use a Fragment wrapper so no extra
  // DOM node is introduced around multi-block content.
  forceBlock: true,
  wrapper: React.Fragment,
  // Untrusted model output: never transcribe raw HTML into JSX.
  disableParsingRawHTML: true,
  sanitizer,
  overrides: {
    a: {
      component: MarkdownLink
    }
  },
  // Route fenced code blocks to the themed ChatCodeBlock, which owns the copy
  // button, language label, and optional highlighter slot.
  renderRule(next, node, _renderChildren, state) {
    if (node.type === RuleType.codeBlock) {
      return /*#__PURE__*/_jsx(ChatCodeBlock, {
        language: node.lang || undefined,
        children: node.text
      }, state.key);
    }
    return next();
  }
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Renders a markdown string to React elements via `markdown-to-jsx`. Output stays
 * XSS-safe by construction (React elements, no `dangerouslySetInnerHTML`); only
 * link/image URLs are guarded, by {@link sanitizer}.
 */
export function renderMarkdown(text) {
  return /*#__PURE__*/_jsx(Markdown, {
    options: markdownOptions,
    children: normalizeMarkdownForRender(text)
  });
}

/**
 * Streaming-aware markdown text part used as the default `renderText`. Repairs
 * incomplete markdown before rendering — via `remend` once it has lazily loaded,
 * and the dep-free fallback (unbalanced-fence completion) until then — so partial
 * syntax arriving mid-stream renders cleanly instead of leaking raw markers.
 */
function StreamingMarkdownText({
  text
}) {
  const repair = useStreamingMarkdownRepair();
  const source = React.useMemo(() => repair(text), [repair, text]);
  return /*#__PURE__*/_jsx(Markdown, {
    options: markdownOptions,
    children: source
  });
}

/**
 * Default `renderText` for the `text` part. Stable module-level identity so it does
 * not churn the headless `TextPart` `useMemo`.
 */
export const renderStreamingMarkdown = text => /*#__PURE__*/_jsx(StreamingMarkdownText, {
  text: text
});
if (process.env.NODE_ENV !== "production") renderStreamingMarkdown.displayName = "renderStreamingMarkdown";